/**
 * Family Calendar (§22.18).
 *
 * Upcoming events grouped by day, a quick add-event form (with a
 * location-aware "notify on arrival" toggle), and .ics import (Apple
 * Calendar export / school schedules). Live Google/Apple OAuth sync is a
 * planned follow-up; ICS import covers the offline path today.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2, Upload, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { calendarApi, type CalendarEvent, type CalendarKind } from '@/api/calendar';
import { usersApi } from '@/api/users';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';

const KINDS: CalendarKind[] = ['school', 'sports', 'music', 'activity', 'doctor', 'homework', 'other'];
const KIND_ICON: Record<CalendarKind, string> = {
  school: '🏫', sports: '⚽', music: '🎵', activity: '🎨', doctor: '🩺', homework: '📚', other: '📌',
};

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}
function timeLabel(e: CalendarEvent): string {
  if (e.all_day) return 'All day';
  return new Date(e.starts_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function CalendarPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const events = useQuery({ queryKey: ['calendar', 'events'], queryFn: () => calendarApi.events(60) });
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const childName = (id: string | null) =>
    id ? children.data?.find((c) => c.id === id)?.name ?? 'Child' : null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] });
  const remove = useMutation({
    mutationFn: (id: string) => calendarApi.remove(id),
    onSuccess: invalidate,
  });

  // Group events by day.
  const grouped = new Map<string, CalendarEvent[]>();
  for (const e of events.data ?? []) {
    const k = dayKey(e.starts_at);
    if (!grouped.has(k)) grouped.set(k, []);
    grouped.get(k)!.push(e);
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.calendar.title')}
        icon={CalendarDays}
        tint="blue"
        description={t('pages.calendar.description')}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_22rem]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="size-4" /> Upcoming
            </CardTitle>
            <CardDescription>Next 60 days.</CardDescription>
          </CardHeader>
          <CardContent>
            {grouped.size === 0 ? (
              <EmptyState
                icon={CalendarDays}
                tint="blue"
                title="No events yet"
                description="Add one on the right, or import an .ics file from Apple Calendar / your school."
              />
            ) : (
              <div className="space-y-5">
                {[...grouped.entries()].map(([day, evs]) => (
                  <div key={day}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--kz-color-ink-muted)]">{day}</p>
                    <ul className="space-y-2">
                      {evs.map((e) => (
                        <li key={e.id} className="flex items-center gap-3 rounded-card border border-[color:var(--kz-color-border-subtle)] p-3">
                          <span className="text-xl">{KIND_ICON[e.kind]}</span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{e.title}</p>
                            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                              {timeLabel(e)}
                              {childName(e.child_id) ? ` · ${childName(e.child_id)}` : ''}
                              {e.location_name ? ` · ${e.location_name}` : ''}
                            </p>
                          </div>
                          {e.notify_on_arrival && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-800">
                              <MapPin className="size-3" /> on arrival
                            </span>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => remove.mutate(e.id)} aria-label="Delete">
                            <Trash2 className="size-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <aside className="space-y-4">
          <AddEventCard onAdded={invalidate} />
          <ImportCard onImported={invalidate} />
        </aside>
      </div>
    </div>
  );
}

function AddEventCard({ onAdded }: { onAdded: () => void }) {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<CalendarKind>('activity');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState('09:00');
  const [location, setLocation] = useState('');
  const [notify, setNotify] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // Combine the picked date + time into an ISO string for submission.
  const startsAt = date
    ? (() => {
        const [hStr, mStr] = time.split(':');
        const h = parseInt(hStr ?? '0', 10);
        const m = parseInt(mStr ?? '0', 10);
        const d = new Date(date);
        d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
        return d.toISOString();
      })()
    : '';

  const create = useMutation({
    mutationFn: () =>
      calendarApi.create({
        title: title.trim(),
        kind,
        starts_at: startsAt,
        location_name: location.trim() || null,
        notify_on_arrival: notify,
      }),
    onSuccess: () => {
      toast.success('Event added.');
      setTitle('');
      setDate(undefined);
      setTime('09:00');
      setLocation('');
      setNotify(false);
      onAdded();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Add failed.'),
  });

  const dateLabel = date
    ? date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Pick a date';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Plus className="size-4" /> Add event</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-1.5">
          <Label htmlFor="ev-title">Title</Label>
          <Input id="ev-title" placeholder="Piano class" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ev-kind">Type</Label>
          <select id="ev-kind" className="flex h-9 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-sm" value={kind} onChange={(e) => setKind(e.target.value as CalendarKind)}>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_ICON[k]} {k}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <div className="grid min-w-0 gap-1.5">
            <Label>Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'flex h-9 w-full items-center gap-2 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-left text-sm',
                    !date && 'text-[color:var(--kz-color-ink-muted)]',
                  )}
                >
                  <CalendarDays className="size-4 shrink-0 text-[color:var(--kz-color-ink-muted)]" />
                  {dateLabel}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setDateOpen(false);
                  }}
                  disabled={{ before: new Date() }}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid min-w-0 gap-1.5">
            <Label htmlFor="ev-time">Time</Label>
            <Input
              id="ev-time"
              type="time"
              className="w-full min-w-0"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ev-loc">Location (optional)</Label>
          <Input id="ev-loc" placeholder="Music school" value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className="flex items-center justify-between rounded-md border border-[color:var(--kz-color-border-subtle)] p-3">
          <div>
            <p className="text-sm font-medium">Notify on arrival</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">Ping me when they arrive at the location.</p>
          </div>
          <Switch checked={notify} onCheckedChange={setNotify} />
        </div>
        <Button onClick={() => create.mutate()} disabled={!title.trim() || !date || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add event'}
        </Button>
      </CardContent>
    </Card>
  );
}

function ImportCard({ onImported }: { onImported: () => void }) {
  const [ics, setIcs] = useState('');
  const [kind, setKind] = useState<CalendarKind>('school');
  const importIcs = useMutation({
    mutationFn: () => calendarApi.importIcs(ics, kind),
    onSuccess: (r) => {
      toast.success(`Imported ${r.imported}, updated ${r.updated}.`);
      setIcs('');
      onImported();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Import failed.'),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Upload className="size-4" /> Import .ics</CardTitle>
        <CardDescription>Paste an Apple Calendar export or school schedule.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <select className="flex h-9 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-sm" value={kind} onChange={(e) => setKind(e.target.value as CalendarKind)}>
          {KINDS.map((k) => <option key={k} value={k}>{KIND_ICON[k]} {k}</option>)}
        </select>
        <textarea
          className="min-h-24 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent p-2 font-mono text-xs"
          placeholder="BEGIN:VCALENDAR…"
          value={ics}
          onChange={(e) => setIcs(e.target.value)}
        />
        <Button variant="outline" onClick={() => importIcs.mutate()} disabled={ics.length < 10 || importIcs.isPending}>
          {importIcs.isPending ? 'Importing…' : 'Import'}
        </Button>
      </CardContent>
    </Card>
  );
}
